/**
 * src/engine/stressPresets.ts — named stress scenarios (§6.5, §6.21).
 *
 * Two families:
 *  • RATE stress presets expand to a SOFR delta path (bps as a function of month).
 *  • ASSET stress presets give per-asset-class instantaneous drops (percent; negative
 *    = a gain, e.g. Treasuries rallying in a crash). Applied as value × (1 − d/100).
 */
import type { AssetClass, StressPreset } from '@/types';

export type RateStressPreset = 'rising_300_24m' | 'falling_200_24m' | 'spike_500_revert' | 'hike_cycle_2022';
export type AssetStressPreset = 'crash_2008' | 'equity_bear' | 'rate_spike_assets';

const RATE_PRESETS: ReadonlySet<string> = new Set<RateStressPreset>([
  'rising_300_24m',
  'falling_200_24m',
  'spike_500_revert',
  'hike_cycle_2022',
]);

export function isRateStressPreset(p: StressPreset): p is RateStressPreset {
  return RATE_PRESETS.has(p);
}

/** SOFR delta in basis points at `monthIndex` for a rate stress preset (§6.5). */
export function rateStressDeltaBps(preset: RateStressPreset, monthIndex: number): number {
  const m = Math.max(0, monthIndex);
  switch (preset) {
    case 'rising_300_24m':
      return 300 * Math.min(m, 24) / 24;
    case 'falling_200_24m':
      return -200 * Math.min(m, 24) / 24;
    case 'spike_500_revert':
      // +500 bps over 12 months, then −500 over the next 24 back to base, then flat.
      if (m <= 12) return 500 * (m / 12);
      if (m <= 36) return 500 * (1 - (m - 12) / 24);
      return 0;
    case 'hike_cycle_2022':
      return 25 * Math.min(m, 16);
  }
}

/** Per-asset-class drop percentages (positive = decline; negative = gain) (§6.21). */
export const ASSET_STRESS_PRESETS: Record<AssetStressPreset, Partial<Record<AssetClass, number>>> = {
  crash_2008: {
    equity: 50,
    broad_etf_large_cap: 45,
    mutual_fund: 40,
    fixed_income: 12,
    treasury: -5,
    cash_mmf: 0,
    illiquid_restricted: 50,
    other: 0,
  },
  equity_bear: {
    equity: 35,
    broad_etf_large_cap: 30,
    mutual_fund: 28,
    fixed_income: 5,
    treasury: 0,
    cash_mmf: 0,
    illiquid_restricted: 35,
    other: 0,
  },
  rate_spike_assets: {
    fixed_income: 15,
    treasury: 10,
    equity: 10,
    broad_etf_large_cap: 10,
    cash_mmf: 0,
    mutual_fund: 8,
    illiquid_restricted: 10,
    other: 0,
  },
};
